import {
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PhoneIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import SmsTestPanel from '../../components/SmsTestPanel/SmsTestPanel';
import api from '../../services/api';
import PendingSmsManagement from '../PendingSmsManagement/PendingSmsManagement';

const SmsManagement = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('format-guide');
  const [showSmsPaymentModal, setShowSmsPaymentModal] = useState(false);
  const [showSmsFormatModal, setShowSmsFormatModal] = useState(false);
  const [showSmsSimulatorModal, setShowSmsSimulatorModal] = useState(false);
  const [showAutoMatchedModal, setShowAutoMatchedModal] = useState(false);
  
  // SMS 시뮬레이터 폼 데이터 상태
  const [smsSimulatorFormData, setSmsSimulatorFormData] = useState({
    message: '',
    phone_number: '',
    received_at: new Date().toISOString().slice(0, 16)
  });

  // SMS 포맷 가이드 조회
  const { data: smsFormatsData } = useQuery({
    queryKey: ['sms-formats'],
    queryFn: () => api.get('/payment/sms/formats')
  });

  // SMS 등록 뮤테이션
  const registerSmsPaymentMutation = useMutation({
    mutationFn: (data) => api.post('/payment/sms-payments', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['sms-payments']);
      queryClient.invalidateQueries(['payment-dashboard']);
      setShowSmsPaymentModal(false);
      alert('SMS 입금내역이 등록되었습니다.');
    },
    onError: (error) => {
      alert(`SMS 등록 중 오류가 발생했습니다: ${error.message || error}`);
    }
  });

  // SMS 시뮬레이터 뮤테이션
  const sendSmsMutation = useMutation({
    mutationFn: (data) => {
      console.log('SMS 전송 API 호출 시작...', data);
      return api.post('/payment/sms/receive', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['sms-payments']);
      queryClient.invalidateQueries(['payment-dashboard']);
      alert('SMS가 성공적으로 전송되었습니다.');
    },
    onError: (error) => {
      console.error('SMS 전송 오류:', error);
      alert(`SMS 전송 중 오류가 발생했습니다: ${error.message || error}`);
    }
  });

  // SMS 파싱 테스트 뮤테이션
  const testSmsParseMutation = useMutation({
    mutationFn: (data) => api.post('/payment/sms/test-parse', data),
    onSuccess: (data) => {
      console.log('파싱 테스트 성공:', data);
    },
    onError: (error) => {
      console.error('파싱 테스트 오류:', error);
      alert(`파싱 테스트 중 오류가 발생했습니다: ${error.message || error}`);
    }
  });

  // SMS 자동 생성 뮤테이션
  const generateSmsMutation = useMutation({
    mutationFn: () => api.post('/payment/sms/generate-test', {}),
    onSuccess: (data) => {
      console.log('SMS 생성 성공:', data);
      const generatedData = data.data;
      setSmsSimulatorFormData({
        message: generatedData.message,
        phone_number: generatedData.phone_number,
        received_at: new Date(generatedData.received_at).toISOString().slice(0, 16)
      });
      alert(`SMS 생성 완료!\n${generatedData.tenant_name}님의 ${generatedData.bank_name} 입금 SMS가 생성되었습니다.`);
    },
    onError: (error) => {
      console.error('SMS 생성 오류:', error);
      alert(`SMS 생성 중 오류가 발생했습니다: ${error.message || error}`);
    }
  });

  // 자동매칭 확인 관련
  const { data: autoMatchedData, refetch: refetchAutoMatched } = useQuery({
    queryKey: ['auto-matched-sms'],
    queryFn: async () => {
      const response = await api.get('/payment/sms/auto-matched');
      return response.data;
    },
    enabled: showAutoMatchedModal
  });

  // 임차인 목록 조회 (자동매칭 확인용)
  const { data: smsTenantsData } = useQuery({
    queryKey: ['sms-tenants'],
    queryFn: () => api.get('/tenants')
  });

  // 자동매칭 확인 뮤테이션
  const verifyMatchMutation = useMutation({
    mutationFn: ({ sms_id, is_correct, notes }) => 
      api.post('/payment/sms/verify-match', { sms_id, is_correct, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries(['auto-matched-sms']);
      refetchAutoMatched();
      alert('매칭 확인이 완료되었습니다.');
    },
    onError: (error) => {
      alert(`매칭 확인 중 오류가 발생했습니다: ${error.message || error}`);
    }
  });

  // 자동매칭 수정 뮤테이션
  const updateMatchMutation = useMutation({
    mutationFn: ({ sms_id, new_tenant_id, notes }) => 
      api.post('/payment/sms/update-match', { sms_id, new_tenant_id, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries(['auto-matched-sms']);
      refetchAutoMatched();
      alert('매칭 정보가 수정되었습니다.');
    },
    onError: (error) => {
      alert(`매칭 수정 중 오류가 발생했습니다: ${error.message || error}`);
    }
  });

  // 임대사업자 목록 조회
  const { data: propertyOwners } = useQuery({
    queryKey: ['property-owners'],
    queryFn: () => api.get('/property-owners')
  });

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">SMS 관리</h1>
        </div>

        {/* 탭 네비게이션 */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('format-guide')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'format-guide'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              SMS 포맷 가이드
            </button>
            <button
              onClick={() => setActiveTab('simulator')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'simulator'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              SMS 시뮬레이터
            </button>
            <button
              onClick={() => setActiveTab('test')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'test'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              SMS 테스트
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'pending'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              미처리 SMS
            </button>
            <button
              onClick={() => {
                setActiveTab('auto-matched');
                setShowAutoMatchedModal(true);
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'auto-matched'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              자동매칭 확인
            </button>
          </nav>
        </div>

        {/* 액션 버튼 */}
        <div className="mb-6 flex justify-end">
          <button
            onClick={() => setShowSmsPaymentModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            SMS 등록
          </button>
        </div>

        {/* 탭 컨텐츠 */}
        <div className="mt-6">
          {activeTab === 'format-guide' && (
            <div className="space-y-4">
              <button
                onClick={() => setShowSmsFormatModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <DocumentTextIcon className="h-4 w-4 mr-2" />
                SMS 포맷 가이드 보기
              </button>
            </div>
          )}

          {activeTab === 'simulator' && (
            <div className="space-y-4">
              <button
                onClick={() => setShowSmsSimulatorModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <PhoneIcon className="h-4 w-4 mr-2" />
                SMS 시뮬레이터 열기
              </button>
            </div>
          )}

          {activeTab === 'test' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  SMS 입금 테스트
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  실제 청구서 데이터를 기반으로 다양한 은행의 SMS 입금알림을 테스트할 수 있습니다.
                </p>
                
                <SmsTestPanel 
                  onSmsSent={() => {
                    queryClient.invalidateQueries(['sms-payments']);
                    queryClient.invalidateQueries(['sms-payments-bank-summary']);
                    queryClient.invalidateQueries(['payment-dashboard']);
                  }}
                />
              </div>
            </div>
          )}

          {activeTab === 'pending' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  미처리 SMS 관리
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  자동 매칭에 실패한 SMS를 수동으로 확인하고 입금처리할 수 있습니다.
                </p>
                
                <PendingSmsManagement />
              </div>
            </div>
          )}

          {activeTab === 'auto-matched' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                자동매칭 확인 기능은 모달로 표시됩니다.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* 모달들 */}
      {showSmsPaymentModal && (
        <SmsPaymentRegistrationModal
          onClose={() => setShowSmsPaymentModal(false)}
          onSubmit={registerSmsPaymentMutation.mutate}
          loading={registerSmsPaymentMutation.isLoading}
          propertyOwners={propertyOwners || []}
        />
      )}

      {showSmsFormatModal && (
        <SmsFormatModal
          onClose={() => setShowSmsFormatModal(false)}
          smsFormats={smsFormatsData || []}
        />
      )}

      {showSmsSimulatorModal && (
        <SmsSimulatorModal
          onClose={() => setShowSmsSimulatorModal(false)}
          onSendSms={sendSmsMutation.mutate}
          onTestParse={testSmsParseMutation.mutate}
          onGenerateSms={generateSmsMutation.mutate}
          sendLoading={sendSmsMutation.isLoading}
          parseLoading={testSmsParseMutation.isLoading}
          generateLoading={generateSmsMutation.isLoading}
          formData={smsSimulatorFormData}
          setFormData={setSmsSimulatorFormData}
        />
      )}

      {showAutoMatchedModal && (
        <AutoMatchedModal
          onClose={() => setShowAutoMatchedModal(false)}
          onVerifyMatch={verifyMatchMutation.mutate}
          onUpdateMatch={updateMatchMutation.mutate}
          verifyLoading={verifyMatchMutation.isLoading}
          updateLoading={updateMatchMutation.isLoading}
          tenants={smsTenantsData?.data || []}
        />
      )}
    </div>
  );
};

// SMS 포맷 가이드 모달 컴포넌트 (PaymentManagement.js에서 복사)
const SmsFormatModal = ({ onClose, smsFormats }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">SMS 포맷 가이드</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          <div className="space-y-4">
            {smsFormats && smsFormats.length > 0 ? (
              smsFormats.map((format, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">{format.bank_name}</h4>
                  <p className="text-sm text-gray-600 mb-2">{format.example_message}</p>
                  <div className="text-xs text-gray-500">
                    <p>파싱 규칙: {format.parsing_rule || 'N/A'}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">등록된 SMS 포맷이 없습니다.</p>
            )}
          </div>
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// SMS 시뮬레이터 모달 컴포넌트 (PaymentManagement.js에서 복사)
const SmsSimulatorModal = ({ onClose, onSendSms, onTestParse, onGenerateSms, sendLoading, parseLoading, generateLoading, formData, setFormData }) => {
  const [activeTab, setActiveTab] = useState('send');
  const [testMessage, setTestMessage] = useState('');
  const [parseResult, setParseResult] = useState(null);

  const smsTemplates = [
    {
      name: "국민은행 입금알림",
      message: "[국민은행] 12/25 14:30 김철수님께 500,000원이 입금되었습니다. 잔액: 1,234,567원",
      phone: "010-1234-5678"
    },
    {
      name: "신한은행 입금알림", 
      message: "신한은행 12/25 14:30 이영희님께 600,000원이 입금되었습니다. 잔액: 2,345,678원",
      phone: "010-2345-6789"
    },
    {
      name: "우리은행 입금알림",
      message: "우리은행 12/25 14:30 박민수님께 700,000원이 입금되었습니다. 잔액: 3,456,789원",
      phone: "010-3456-7890"
    },
    {
      name: "하나은행 입금알림",
      message: "하나은행 12/25 14:30 최지영님께 800,000원이 입금되었습니다. 잔액: 4,567,890원",
      phone: "010-4567-8901"
    },
    {
      name: "농협은행 입금알림",
      message: "농협 12/25 14:30 정민호님께 450,000원이 입금되었습니다. 잔액: 5,678,901원",
      phone: "010-5678-9012"
    },
    {
      name: "기업은행 입금알림",
      message: "기업은행 12/25 14:30 한소영님께 350,000원이 입금되었습니다. 잔액: 6,789,012원",
      phone: "010-6789-0123"
    }
  ];

  const handleSendSms = (e) => {
    e.preventDefault();
    if (!formData.message.trim()) {
      alert('SMS 메시지를 입력해주세요.');
      return;
    }

    const smsData = {
      message: formData.message,
      phone_number: formData.phone_number || '010-0000-0000',
      received_at: new Date(formData.received_at).toISOString()
    };
    
    onSendSms(smsData);
  };

  const handleTestParse = () => {
    if (!testMessage.trim()) {
      alert('테스트할 SMS 메시지를 입력해주세요.');
      return;
    }

    onTestParse({ message: testMessage }, {
      onSuccess: (data) => {
        setParseResult(data.data);
      },
      onError: (error) => {
        console.error('파싱 테스트 오류:', error);
        alert('파싱 테스트 중 오류가 발생했습니다.');
      }
    });
  };

  const handleTemplateSelect = (template) => {
    if (activeTab === 'send') {
      setFormData({ 
        ...formData, 
        message: template.message,
        phone_number: template.phone 
      });
    } else {
      setTestMessage(template.message);
    }
  };

  const handleGenerateSms = () => {
    onGenerateSms();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">SMS 시뮬레이터</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('send')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'send'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                SMS 전송
              </button>
              <button
                onClick={() => setActiveTab('parse')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'parse'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                파싱 테스트
              </button>
            </nav>
          </div>

          {activeTab === 'send' && (
            <form onSubmit={handleSendSms} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SMS 메시지
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  rows="4"
                  placeholder="SMS 메시지를 입력하세요..."
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    수신 전화번호
                  </label>
                  <input
                    type="tel"
                    value={formData.phone_number}
                    onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="010-1234-5678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    수신 시간
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.received_at}
                    onChange={(e) => setFormData({...formData, received_at: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={handleGenerateSms}
                  disabled={generateLoading}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  {generateLoading ? '생성 중...' : '자동 생성'}
                </button>
                <button
                  type="submit"
                  disabled={sendLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {sendLoading ? '전송 중...' : 'SMS 전송'}
                </button>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  템플릿 선택
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {smsTemplates.map((template, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleTemplateSelect(template)}
                      className="text-left p-3 border border-gray-200 rounded-md hover:bg-gray-50 text-sm"
                    >
                      <div className="font-medium text-gray-900">{template.name}</div>
                      <div className="text-xs text-gray-500 mt-1 truncate">{template.message}</div>
                    </button>
                  ))}
                </div>
              </div>
            </form>
          )}

          {activeTab === 'parse' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  테스트할 SMS 메시지
                </label>
                <textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  rows="4"
                  placeholder="파싱할 SMS 메시지를 입력하세요..."
                />
              </div>

              <button
                onClick={handleTestParse}
                disabled={parseLoading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {parseLoading ? '파싱 중...' : '파싱 테스트'}
              </button>

              {parseResult && (
                <div className="mt-4 p-4 bg-gray-50 rounded-md">
                  <h4 className="font-medium text-gray-900 mb-2">파싱 결과</h4>
                  <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                    {JSON.stringify(parseResult, null, 2)}
                  </pre>
                </div>
              )}

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  템플릿 선택
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {smsTemplates.map((template, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleTemplateSelect(template)}
                      className="text-left p-3 border border-gray-200 rounded-md hover:bg-gray-50 text-sm"
                    >
                      <div className="font-medium text-gray-900">{template.name}</div>
                      <div className="text-xs text-gray-500 mt-1 truncate">{template.message}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 자동매칭 확인 모달 컴포넌트 (PaymentManagement.js에서 복사)
const AutoMatchedModal = ({ onClose, onVerifyMatch, onUpdateMatch, verifyLoading, updateLoading, tenants }) => {
  const { data: autoMatchedData, isLoading } = useQuery({
    queryKey: ['auto-matched-sms'],
    queryFn: async () => {
      const response = await api.get('/payment/sms/auto-matched');
      return response.data;
    }
  });

  const autoMatchedSms = autoMatchedData?.data || [];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              자동매칭 확인 ({autoMatchedSms.length}건)
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500">로딩 중...</p>
            </div>
          ) : autoMatchedSms.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500">자동매칭된 SMS가 없습니다.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">시간</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">전화번호</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">송금자</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">금액</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">매칭된 임차인</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">액션</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {autoMatchedSms.map((sms) => (
                    <tr key={sms.id}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {new Date(sms.transaction_time).toLocaleString('ko-KR')}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {sms.tenant_phone}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {sms.sender_name || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                        {sms.amount.toLocaleString()}원
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {sms.matched_tenant?.name || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex space-x-2 justify-center">
                          <button
                            onClick={() => onVerifyMatch({ sms_id: sms.id, is_correct: true })}
                            disabled={verifyLoading}
                            className="text-green-600 hover:text-green-800 text-sm font-medium disabled:opacity-50"
                          >
                            확인
                          </button>
                          <button
                            onClick={() => onVerifyMatch({ sms_id: sms.id, is_correct: false })}
                            disabled={verifyLoading}
                            className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                          >
                            오류
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// SMS 등록 모달 컴포넌트 (PaymentManagement.js에서 복사)
const SmsPaymentRegistrationModal = ({ onClose, onSubmit, loading, propertyOwners = [] }) => {
  const [formData, setFormData] = useState({
    tenant_phone: '',
    tenant_name: '',
    amount: '',
    bank_name: '',
    account_number: '',
    sender_name: '',
    property_owner_id: '',
    transaction_time: new Date().toISOString().slice(0, 16),
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">SMS 입금 등록</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">입금자 전화번호</label>
              <input
                type="tel"
                value={formData.tenant_phone}
                onChange={(e) => setFormData({...formData, tenant_phone: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">임차인 이름 (선택 사항)</label>
              <input
                type="text"
                value={formData.tenant_name}
                onChange={(e) => setFormData({...formData, tenant_name: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="임차인 이름"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">입금금액</label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">은행명</label>
              <input
                type="text"
                value={formData.bank_name}
                onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">계좌번호</label>
              <input
                type="text"
                value={formData.account_number}
                onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">거래시간</label>
              <input
                type="datetime-local"
                value={formData.transaction_time}
                onChange={(e) => setFormData({...formData, transaction_time: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">송금자명</label>
              <input
                type="text"
                value={formData.sender_name}
                onChange={(e) => setFormData({...formData, sender_name: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            {propertyOwners.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700">임대사업자</label>
                <select
                  value={formData.property_owner_id}
                  onChange={(e) => setFormData({...formData, property_owner_id: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                >
                  <option value="">선택하세요</option>
                  {propertyOwners.map(owner => (
                    <option key={owner.id} value={owner.id}>{owner.company_name || owner.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700">메모</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                rows="3"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? '등록 중...' : '등록'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SmsManagement;






